// Zeit-Helfer für geplante Nachrichten und Erinnerungen (lokale Zeit des Browsers, Ausgabe als Date/ISO).
import { ErrorCodes } from '@lagerfeuer/shared';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../lib/api';
import { errorMessage } from '../format';

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
/** Mindestabstand für geplante Nachrichten laut Vertrag (3.9). */
export const SCHEDULE_MIN_LEAD_MS = 30_000;
/** Höchstabstand für geplante Nachrichten laut Vertrag (3.9): 1 Jahr. */
export const SCHEDULE_MAX_LEAD_MS = 365 * DAY;

/** Jetzt + n Minuten, auf die volle Minute abgerundet (passt zu `datetime-local`). */
export function inMinutes(n: number, now: number = Date.now()): Date {
  return new Date(Math.floor((now + n * MINUTE) / MINUTE) * MINUTE);
}

/** Datum `dayOffset` Tage nach heute um hh:mm (lokal). */
export function atDayTime(dayOffset: number, hours: number, minutes = 0, now: number = Date.now()): Date {
  const d = new Date(now);
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

/** Heute um hh:mm; liegt das (fast) in der Vergangenheit, morgen um hh:mm. */
export function nextAt(hours: number, minutes = 0, now: number = Date.now(), leadMs = SCHEDULE_MIN_LEAD_MS): Date {
  const today = atDayTime(0, hours, minutes, now);
  return today.getTime() > now + leadMs ? today : atDayTime(1, hours, minutes, now);
}

/** Nächster Wochentag (0 = So, 1 = Mo …) strikt nach heute um hh:mm. */
export function nextWeekday(weekday: number, hours: number, minutes = 0, now: number = Date.now()): Date {
  const d = new Date(now);
  let diff = (weekday - d.getDay() + 7) % 7;
  if (diff === 0) diff = 7;
  return atDayTime(diff, hours, minutes, now);
}

/** Prüft einen Sendezeitpunkt für geplante Nachrichten. Rückgabe: Fehlertext oder null. */
export function validateSendAt(iso: string | null, now: number = Date.now()): string | null {
  if (!iso) return 'Bitte einen Zeitpunkt wählen.';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Ungültiger Zeitpunkt.';
  if (t <= now + SCHEDULE_MIN_LEAD_MS) return 'Der Zeitpunkt muss mindestens 30 Sekunden in der Zukunft liegen.';
  if (t > now + SCHEDULE_MAX_LEAD_MS) return 'Der Zeitpunkt darf höchstens ein Jahr in der Zukunft liegen.';
  return null;
}

/** Prüft einen Erinnerungszeitpunkt (nur „in der Zukunft"). */
export function validateRemindAt(iso: string | null, now: number = Date.now()): string | null {
  if (!iso) return 'Bitte einen Zeitpunkt wählen.';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Ungültiger Zeitpunkt.';
  if (t <= now) return 'Der Zeitpunkt muss in der Zukunft liegen.';
  return null;
}

export function isLimitReached(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.code === ErrorCodes.LIMIT_REACHED;
}

/** Verständliche Fehlermeldung, mit Sondertext für `403 LIMIT_REACHED`. */
export function limitAwareMessage(err: unknown, limitText: string): string {
  if (isLimitReached(err)) {
    const detail = err instanceof ApiError && err.message ? ` (${err.message})` : '';
    return `${limitText}${detail}`;
  }
  return errorMessage(err);
}

/** Aktuelle Zeit, die sich alle `intervalMs` aktualisiert (für relative Angaben/„überfällig"). */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Überschrift für eine Tagesgruppe („Heute", „Morgen", „Gestern" oder Datum). */
export function dayLabel(iso: string, now: number = Date.now()): string {
  const d = new Date(iso);
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(d) - start(new Date(now))) / DAY);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  if (diff === -1) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/** Schlüssel YYYY-MM-DD (lokal) für Gruppierung nach Tag. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Kurzer Textauszug (eine Zeile). */
export function excerpt(text: string | null | undefined, max = 120): string {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}
